<template>
  <div class="admin-info-cards">
    <div class="admin-header">
      <h2>Info Cards Yönetimi</h2>
      <button @click="showAddForm = true" class="btn btn-primary">
        <i class="fas fa-plus"></i> Yeni Kart Ekle
      </button>
    </div>

    <!-- Filtreler -->
    <div class="admin-filters">
      <div class="filter-group">
        <label>Durum:</label>
        <select v-model="filterActive" @change="loadInfoCards">
          <option value="all">Tümü</option>
          <option value="true">Aktif</option>
          <option value="false">Pasif</option>
        </select>
      </div>
      <div class="filter-group">
        <label>Kart Türü:</label>
        <select v-model="filterType">
          <option value="all">Tüm Türler</option>
          <option value="yellow-card">Sarı Kart</option>
          <option value="black-card">Siyah Kart</option>
          <option value="image-card">Resimli Kart</option>
        </select>
      </div>
    </div>

    <!-- İstatistikler -->
    <div class="admin-stats">
      <div class="stat-card">
        <h3>{{ filteredInfoCards.length }}</h3>
        <p>Toplam Kart</p>
      </div>
      <div class="stat-card">
        <h3>{{ activeCount }}</h3>
        <p>Aktif Kart</p>
      </div>
      <div class="stat-card">
        <h3>{{ inactiveCount }}</h3>
        <p>Pasif Kart</p>
      </div>
    </div>

    <!-- Yükleniyor -->
    <div v-if="isLoading" class="loading">
      <i class="fas fa-spinner fa-spin"></i> Yükleniyor...
    </div>

    <!-- Info Cards Listesi -->
    <div v-else class="admin-table-container">
      <table class="admin-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Başlık (TR)</th>
            <th>Başlık (EN)</th>
            <th>Kart Türü</th>
            <th>Durum</th>
            <th>Sıra</th>
            <th>İşlemler</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="card in filteredInfoCards" :key="card.id">
            <td>{{ card.id }}</td>
            <td>{{ card.title_tr }}</td>
            <td>{{ card.title_en }}</td>
            <td>
              <span class="card-type-badge" :class="card.card_type">
                {{ getCardTypeLabel(card.card_type) }}
              </span>
            </td>
            <td>
              <span 
                class="status-badge" 
                :class="getStatusBadgeClass(card.is_active)"
              >
                {{ getStatusText(card.is_active) }}
              </span>
            </td>
            <td>{{ card.sort_order }}</td>
            <td class="actions">
              <button 
                @click="editCard(card)" 
                class="btn btn-sm btn-secondary"
                title="Düzenle"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button 
                @click="toggleStatus(card)" 
                class="btn btn-sm" 
                :class="card.is_active ? 'btn-warning' : 'btn-success'"
                :title="card.is_active ? 'Pasif Yap' : 'Aktif Yap'"
              >
                <i class="fas" :class="card.is_active ? 'fa-eye-slash' : 'fa-eye'"></i>
              </button>
              <button 
                @click="deleteCard(card)" 
                class="btn btn-sm btn-danger"
                title="Sil"
              >
                <i class="fas fa-trash"></i>
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Kart Ekleme/Düzenleme Modal -->
    <div v-if="showAddForm || editingCard" class="modal-overlay" @click="closeModal">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h3>{{ editingCard ? 'Kart Düzenle' : 'Yeni Kart Ekle' }}</h3>
          <button @click="closeModal" class="close-btn">×</button>
        </div>
        
        <form @submit.prevent="saveCard" class="card-form">
          <div class="form-row">
            <div class="form-group">
              <label>Başlık (Türkçe) *</label>
              <input 
                v-model="formData.title_tr" 
                type="text" 
                required 
                maxlength="255"
              >
            </div>
            <div class="form-group">
              <label>Başlık (İngilizce) *</label>
              <input 
                v-model="formData.title_en" 
                type="text" 
                required 
                maxlength="255"
              >
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Açıklama (Türkçe) *</label>
              <textarea 
                v-model="formData.description_tr" 
                required 
                rows="3"
              ></textarea>
            </div>
            <div class="form-group">
              <label>Açıklama (İngilizce) *</label>
              <textarea 
                v-model="formData.description_en" 
                required 
                rows="3"
              ></textarea>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Buton Metni (Türkçe)</label>
              <input 
                v-model="formData.button_text_tr" 
                type="text" 
                maxlength="100"
                placeholder="Detaylar"
              >
            </div>
            <div class="form-group">
              <label>Buton Metni (İngilizce)</label>
              <input 
                v-model="formData.button_text_en" 
                type="text" 
                maxlength="100"
                placeholder="Details"
              >
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Kart Türü *</label>
              <select v-model="formData.card_type" required>
                <option value="yellow-card">Sarı Kart</option>
                <option value="black-card">Siyah Kart</option>
                <option value="image-card">Resimli Kart</option>
                <option value="default">Varsayılan</option>
              </select>
            </div>
            <div class="form-group">
              <label>Buton Aksiyonu</label>
              <select v-model="formData.button_action">
                <option value="contact">İletişim</option>
                <option value="services">Hizmetler</option>
                <option value="projects">Projeler</option>
                <option value="about">Hakkımızda</option>
              </select>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Arka Plan Resmi URL</label>
              <input 
                v-model="formData.background_image" 
                type="url" 
                placeholder="https://example.com/image.jpg"
              >
            </div>
            <div class="form-group">
              <label>Icon</label>
              <input 
                v-model="formData.icon" 
                type="text" 
                placeholder="🚀"
              >
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Arka Plan Rengi</label>
              <input 
                v-model="formData.background_color" 
                type="color"
              >
            </div>
            <div class="form-group">
              <label>Metin Rengi</label>
              <input 
                v-model="formData.text_color" 
                type="color"
              >
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label>Sıralama</label>
              <input 
                v-model.number="formData.sort_order" 
                type="number" 
                min="0"
              >
            </div>
            <div class="form-group">
              <label class="checkbox-label">
                <input 
                  v-model="formData.is_active" 
                  type="checkbox"
                >
                Aktif
              </label>
            </div>
          </div>

          <div class="form-actions">
            <button type="button" @click="closeModal" class="btn btn-secondary">
              İptal
            </button>
            <button type="submit" class="btn btn-primary" :disabled="isSaving">
              {{ isSaving ? 'Kaydediliyor...' : 'Kaydet' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { adminApi } from '@/services/adminApi'

// Reactive data
const infoCards = ref([])
const isLoading = ref(false)
const isSaving = ref(false)
const showAddForm = ref(false)
const editingCard = ref(null)
const filterActive = ref('all')
const filterType = ref('all')

// Form data
const formData = ref({
  title_tr: '',
  title_en: '',
  description_tr: '',
  description_en: '',
  button_text_tr: 'Detaylar',
  button_text_en: 'Details',
  button_action: 'contact',
  card_type: 'yellow-card',
  background_image: '',
  icon: '',
  background_color: '',
  text_color: '',
  is_active: true,
  sort_order: 0
})

// Computed properties
const filteredInfoCards = computed(() => {
  let filtered = infoCards.value
  
  // Durum filtresi
  if (filterActive.value !== 'all') {
    const isActive = filterActive.value === 'true'
    filtered = filtered.filter(card => {
      const cardActive = card.is_active === '1' || card.is_active === 1 || card.is_active === true
      return cardActive === isActive
    })
  }
  
  // Tür filtresi
  if (filterType.value !== 'all') {
    filtered = filtered.filter(card => card.card_type === filterType.value)
  }
  
  return filtered
})

const activeCount = computed(() => {
  return infoCards.value.filter(card => {
    return card.is_active === '1' || card.is_active === 1 || card.is_active === true
  }).length
})

const inactiveCount = computed(() => {
  return infoCards.value.filter(card => {
    return card.is_active === '0' || card.is_active === 0 || card.is_active === false
  }).length
})

// Methods
const loadInfoCards = async () => {
  try {
    isLoading.value = true
    console.log('🔄 Loading info cards for admin...')
    
    const response = await adminApi.getInfoCards()
    console.log('📋 Admin info cards response:', response)
    console.log('📋 Response type:', typeof response, 'Is array:', Array.isArray(response))
    
    if (response && Array.isArray(response)) {
      infoCards.value = response
      console.log('✅ Direct array assigned:', infoCards.value.length, 'cards')
    } else if (response && response.data && Array.isArray(response.data)) {
      infoCards.value = response.data
      console.log('✅ Data array assigned:', infoCards.value.length, 'cards')
    } else {
      console.warn('⚠️ Invalid response format:', response)
      infoCards.value = []
    }
    
    console.log('✅ Final info cards:', infoCards.value)
  } catch (error) {
    console.error('❌ Info cards yüklenemedi:', error)
    alert('Info cards yüklenirken hata oluştu: ' + error.message)
    infoCards.value = []
  } finally {
    isLoading.value = false
  }
}

const getCardTypeLabel = (type) => {
  const labels = {
    'yellow-card': 'Sarı Kart',
    'black-card': 'Siyah Kart',
    'image-card': 'Resimli Kart',
    'default': 'Varsayılan'
  }
  return labels[type] || type
}

const getStatusBadgeClass = (isActive) => {
  const active = isActive === '1' || isActive === 1 || isActive === true
  return active ? 'status-active' : 'status-inactive'
}

const getStatusText = (isActive) => {
  const active = isActive === '1' || isActive === 1 || isActive === true
  return active ? 'Aktif' : 'Pasif'
}

const resetForm = () => {
  formData.value = {
    title_tr: '',
    title_en: '',
    description_tr: '',
    description_en: '',
    button_text_tr: 'Detaylar',
    button_text_en: 'Details',
    button_action: 'contact',
    card_type: 'yellow-card',
    background_image: '',
    icon: '',
    background_color: '',
    text_color: '',
    is_active: true,
    sort_order: 0
  }
}

const editCard = (card) => {
  editingCard.value = card
  formData.value = {
    title_tr: card.title_tr || '',
    title_en: card.title_en || '',
    description_tr: card.description_tr || '',
    description_en: card.description_en || '',
    button_text_tr: card.button_text_tr || 'Detaylar',
    button_text_en: card.button_text_en || 'Details',
    button_action: card.button_action || 'contact',
    card_type: card.card_type || 'yellow-card',
    background_image: card.background_image || '',
    icon: card.icon || '',
    background_color: card.background_color || '',
    text_color: card.text_color || '',
    is_active: card.is_active === '1' || card.is_active === 1 || card.is_active === true,
    sort_order: parseInt(card.sort_order) || 0
  }
}

const closeModal = () => {
  showAddForm.value = false
  editingCard.value = null
  resetForm()
}

const saveCard = async () => {
  try {
    isSaving.value = true
    
    const cardData = {
      ...formData.value,
      is_active: formData.value.is_active ? 1 : 0
    }
    
    if (editingCard.value) {
      // Güncelleme
      await adminApi.updateInfoCard(editingCard.value.id, cardData)
      alert('Kart başarıyla güncellendi!')
    } else {
      // Yeni ekleme
      await adminApi.createInfoCard(cardData)
      alert('Yeni kart başarıyla eklendi!')
    }
    
    closeModal()
    await loadInfoCards()
  } catch (error) {
    console.error('❌ Kart kaydedilemedi:', error)
    alert('Kart kaydedilirken hata oluştu!')
  } finally {
    isSaving.value = false
  }
}

const toggleStatus = async (card) => {
  try {
    const currentStatus = card.is_active === '1' || card.is_active === 1 || card.is_active === true
    const newStatus = !currentStatus
    
    console.log('Toggling status:', { currentStatus, newStatus, cardId: card.id })
    
    await adminApi.updateInfoCard(card.id, { 
      is_active: newStatus ? 1 : 0 
    })
    
    alert(`Kart ${newStatus ? 'aktif' : 'pasif'} yapıldı!`)
    await loadInfoCards()
  } catch (error) {
    console.error('❌ Durum değiştirilemedi:', error)
    alert('Durum değiştirilirken hata oluştu: ' + error.message)
  }
}

const deleteCard = async (card) => {
  if (!confirm(`"${card.title_tr}" kartını silmek istediğinizden emin misiniz?\n\nBu işlem geri alınamaz!`)) {
    return
  }
  
  try {
    console.log('Deleting card:', card.id)
    await adminApi.deleteInfoCard(card.id)
    alert('Kart başarıyla silindi!')
    await loadInfoCards()
  } catch (error) {
    console.error('❌ Kart silinemedi:', error)
    alert('Kart silinirken hata oluştu: ' + error.message)
  }
}

// Watchers
watch(filterType, () => {
  console.log('🔍 Filter type changed:', filterType.value)
})

// Lifecycle
onMounted(() => {
  loadInfoCards()
})
</script>

<style scoped>
.admin-info-cards {
  padding: 20px;
}

.admin-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.admin-header h2 {
  margin: 0;
  color: #333;
}

.admin-filters {
  display: flex;
  gap: 20px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.filter-group label {
  font-weight: 500;
  color: #555;
  font-size: 14px;
}

.filter-group select {
  padding: 8px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
}

.admin-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
}

.stat-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.stat-card h3 {
  font-size: 2rem;
  margin: 0 0 5px 0;
  font-weight: bold;
}

.stat-card p {
  margin: 0;
  opacity: 0.9;
  font-size: 14px;
}

.loading {
  text-align: center;
  padding: 40px;
  color: #666;
}

.admin-table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.admin-table {
  width: 100%;
  border-collapse: collapse;
}

.admin-table th,
.admin-table td {
  padding: 12px;
  text-align: left;
  border-bottom: 1px solid #eee;
}

.admin-table th {
  background: #f8f9fa;
  font-weight: 500;
  color: #555;
  font-size: 14px;
}

.admin-table tr:hover {
  background: #f8f9fa;
}

.card-type-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
}

.card-type-badge.yellow-card {
  background: #fff3cd;
  color: #856404;
}

.card-type-badge.black-card {
  background: #d1ecf1;
  color: #0c5460;
}

.card-type-badge.image-card {
  background: #d4edda;
  color: #155724;
}

.status-badge {
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.status-active {
  background: #d4edda;
  color: #155724;
}

.status-inactive {
  background: #f8d7da;
  color: #721c24;
}

.actions {
  display: flex;
  gap: 5px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  transition: all 0.2s;
}

.btn-primary {
  background: #007bff;
  color: white;
}

.btn-primary:hover {
  background: #0056b3;
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background: #545b62;
}

.btn-success {
  background: #28a745;
  color: white;
}

.btn-success:hover {
  background: #1e7e34;
}

.btn-warning {
  background: #ffc107;
  color: #212529;
}

.btn-warning:hover {
  background: #e0a800;
}

.btn-danger {
  background: #dc3545;
  color: white;
}

.btn-danger:hover {
  background: #c82333;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #eee;
}

.modal-header h3 {
  margin: 0;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #999;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #333;
}

.card-form {
  padding: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.form-group label {
  font-weight: 500;
  color: #555;
  font-size: 14px;
}

.form-group input,
.form-group textarea,
.form-group select {
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.form-group input:focus,
.form-group textarea:focus,
.form-group select:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.checkbox-label {
  flex-direction: row !important;
  align-items: center;
  gap: 8px;
  cursor: pointer;
}

.checkbox-label input[type="checkbox"] {
  width: auto;
  margin: 0;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

@media (max-width: 768px) {
  .admin-filters {
    flex-direction: column;
  }
  
  .form-row {
    grid-template-columns: 1fr;
  }
  
  .admin-stats {
    grid-template-columns: 1fr;
  }
  
  .actions {
    flex-direction: column;
  }
}
</style>